import React, { forwardRef, useEffect } from "react";
import { cva, cx } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { EYEBROW } from "../../lib/typography.js";
import { TrendIndicator } from "../TrendIndicator/index.js";

/* ---------------- KPIValue ---------------- */

/** The figure's scale: a 14, 18, 24 or 32px value. `md` is today's figure. */
export type KPIValueSize = "xs" | "sm" | "md" | "lg";
/** `eyebrow` (the default): the shared uppercase EYEBROW. `label`: a
 *  sentence-case caption. */
export type KPIValueVariant = "eyebrow" | "label";
/** `stacked` (the default): the label above the figure. `horizontal`: the
 *  figure, then the label, on one baseline row. */
export type KPIValueLayout = "stacked" | "horizontal";

/* The value and its unit step together; md is today's pair. Joined with cx,
   not cn: tailwind-merge drops a leading-* written before a font size. */
const valueSize = cva(undefined, {
  variants: {
    size: { xs: "text-sm", sm: "text-lg", md: "text-2xl", lg: "text-display" },
  },
  defaultVariants: { size: "md" },
});

const unitSize = cva(undefined, {
  variants: {
    size: { xs: "text-dense-1", sm: "text-xs", md: "text-sm", lg: "text-base" },
  },
  defaultVariants: { size: "md" },
});

const labelStyle = cva(undefined, {
  variants: {
    variant: { eyebrow: EYEBROW, label: "text-caption text-pb-text-muted" },
  },
  defaultVariants: { variant: "eyebrow" },
});

export interface KPIValueProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The figure. Omitted (or null): no figure is drawn, so a label-only
   *  KPIValue (a status tile's heading) renders no empty value. */
  value?: React.ReactNode;
  unit?: React.ReactNode;
  label?: React.ReactNode;
  delta?: number;
  inverse?: boolean;
  size?: KPIValueSize | undefined;
  /** Colours the value only, from the one tone map (lib/tones.ts). Omitted:
   *  the default text colour. */
  tone?: StatusTone | undefined;
  /** The label's style. */
  variant?: KPIValueVariant | undefined;
  layout?: KPIValueLayout | undefined;
  /** A muted caption line under the value. */
  description?: React.ReactNode;
  /** Rendered in place of the TrendIndicator, for a trend that is not a
   *  number (a StatusPill, a worded TrendIndicator). Wins over `delta`. */
  deltaSlot?: React.ReactNode;
  /** The TrendIndicator's unit; it defaults to `%`. */
  deltaUnit?: React.ReactNode;
}

let hasWarnedDeltaConflict = false;

export const KPIValue = forwardRef<HTMLDivElement, KPIValueProps>(
  function KPIValue(
    {
      value,
      unit,
      label,
      delta,
      inverse,
      size,
      tone,
      variant,
      layout = "stacked",
      description,
      deltaSlot,
      deltaUnit,
      className,
      ...rest
    },
    ref,
  ) {
    const hasDeltaSlot = deltaSlot != null && deltaSlot !== false;
    const deltaConflict = hasDeltaSlot && delta !== undefined;
    useEffect(() => {
      if (deltaConflict && !hasWarnedDeltaConflict) {
        console.warn(
          "[KPIValue] `delta` is ignored when `deltaSlot` is set; the slot " +
            "renders in the TrendIndicator's place.",
        );
        hasWarnedDeltaConflict = true;
      }
    }, [deltaConflict]);

    const labelNode = label && (
      <div className={labelStyle({ variant })}>{label}</div>
    );
    const horizontal = layout === "horizontal";
    const hasValue = value != null && value !== false;
    /* The figure row is dropped only when nothing would sit in it, so a
       KPIValue with a value renders today's DOM. */
    const hasRow =
      hasValue ||
      Boolean(unit) ||
      (horizontal && Boolean(labelNode)) ||
      hasDeltaSlot ||
      delta !== undefined;
    return (
      <div ref={ref} className={cn("flex flex-col gap-1", className)} {...rest}>
        {!horizontal && labelNode}
        {hasRow && (
          <div className="flex items-baseline gap-1.5">
            {hasValue && (
              <span
                className={cx(
                  valueSize({ size }),
                  "font-semibold",
                  tone ? TONES[tone].text : "text-pb-text",
                  "pb-tabular leading-none",
                )}
              >
                {value}
              </span>
            )}
            {unit && (
              <span className={cx(unitSize({ size }), "text-pb-text-muted")}>
                {unit}
              </span>
            )}
            {horizontal && labelNode}
            {hasDeltaSlot
              ? deltaSlot
              : delta !== undefined && (
                  <TrendIndicator
                    delta={delta}
                    inverse={inverse}
                    unit={deltaUnit}
                  />
                )}
          </div>
        )}
        {description && (
          <div className="text-caption text-pb-text-muted">{description}</div>
        )}
      </div>
    );
  },
);
